import {
  urlSituacionTerapeutica,
  urlValidarAfiliadoPost,
  urlValidarAfiliadoV1,
} from '../../actions/urls';
import { apiManagerRequest } from '../../axios';
import ERRORS from '../../utils/errors';
import { store } from '../../store';
import getGuid from '../../utils/getGuid';
import Session from '../../utils/session';

const AffiliateService = {
  getRecaptchaToken: () => {
    const {
      auth: { recaptchaToken },
    } = store.getState();
    return recaptchaToken;
  },
  getAffiliateData: async (contra, inte, validador = null) => {
    const { codPrestador } = Session.getAuth().modelEspecifico;
    const url = urlValidarAfiliadoPost.replace(':code', codPrestador);
    const { data } = await apiManagerRequest.post(url, {
      contra,
      inte,
      validador,
      recaptchaResponse: AffiliateService.getRecaptchaToken(),
      apiKey: window.REACT_APP_API_KEY_CAPTCHA,
      deviceid: getGuid(),
    });

    return data;
  },
  getConditionIva: async (affiliate, contract) => {
    const { documento: dni } = affiliate;
    const { data: taxData } = await apiManagerRequest.get(
      `${urlValidarAfiliadoV1}/datos-fiscales/${dni}`,
    );

    if (!taxData.datosFiscales || !Array.isArray(taxData.datosFiscales)) {
      // no tiene DNI, recien nacido por ejemplo
      return null;
    }

    // exclude unnecessary items
    const correctTaxData = taxData.datosFiscales.find(
      (item) => item.pk.contra.trim() === contract && ![4, 9, 10, 11].includes(item.pk.prepaga),
    );

    return correctTaxData?.condicionImpuestos ?? null;
  },
  validate: async (contract, member, validator, needTherapeuticData = false) => {
    try {
      const { affiliate, credential, virtualCredential, plan } =
        await AffiliateService.getAffiliateData(contract, member, validator);

      if (affiliate.estado != '1') {
        // usuario bloqueado
        return {
          afiliado: affiliate,
          isValid: false,
        };
      }

      const isValidCredential = !!!credential.error;

      if (!isValidCredential) {
        // credencial inexistente o algun otro error
        return {
          afiliado: {
            nombreCompleto: '',
            plan: '',
            estadoDesc: credential.error_desc ?? 'Error en validación',
          },
          isValid: false,
          isFetching: false,
        };
      }

      const virtualCredentialNumber =
        virtualCredential.selfie && !!virtualCredential.credencial
          ? virtualCredential.credencial.id
          : null;

      const result = {
        afiliado: affiliate,
        isValid: true,
        plan,
        virtualCredentialNumber,
        presmed: {
          prepaga: credential?.prepaga ?? 0,
          contra: credential?.contra ?? '',
          inte: credential?.inte ?? '',
        },
        iva: await AffiliateService.getConditionIva(affiliate, contract),
      };

      if (needTherapeuticData) {
        const idPrepaid = affiliate.prepaga.id;

        const { data: therapeuticData } = await apiManagerRequest.get(
          `${urlSituacionTerapeutica}/${idPrepaid}/${contract}/${member}`,
        );

        Object.assign(result, { situacionTerapeutica: therapeuticData.requiereToken });
      }

      return result;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;

      if (errorMessage === ERRORS.AFFILIATE.NOT_FOUND) {
        return {
          afiliado: {
            estadoDesc: 'Credencial Inexistente',
          },
          isValid: false,
        };
      }
      throw new Error(error);
    }
  },
  validateSimple: async (contract, member) => {
    try {
      const { affiliate, virtualCredential, plan } = await AffiliateService.getAffiliateData(
        contract,
        member,
      );

      if (affiliate.estado != '1') {
        // usuario bloqueado
        return {
          afiliado: {
            nombreCompleto: `${affiliate.nombre} ${affiliate.apellido}`,
            plan: '',
            estadoDesc: affiliate.estadoDesc ?? 'Credencial Inexistente',
          },
          isValid: false,
        };
      }

      const fullCredential = virtualCredential?.credencial?.id ?? '';
      const virtualCredentialNumber =
        virtualCredential.selfie && !!virtualCredential.credencial
          ? virtualCredential.credencial.id
          : null;

      return {
        afiliado: {
          ...affiliate,
          fullCredential,
        },
        virtualCredentialNumber,
        isValid: true,
        plan,
      };
    } catch (error) {
      const errorMessage = error?.response?.data?.message;

      if (errorMessage === ERRORS.AFFILIATE.NOT_FOUND) {
        return {
          afiliado: {
            estadoDesc: 'Credencial Inexistente',
          },
          isValid: false,
        };
      }
      throw new Error(error);
    }
  },
};

export default AffiliateService;
